import React, { useEffect, useRef } from "react";

/**
 * Reusable confirmation modal — drop-in replacement for `window.confirm`.
 *
 * Props:
 *   isVisible      — show/hide
 *   title          — heading text (optional)
 *   message        — body text or any ReactNode (optional)
 *   confirmText    — confirm button label (default: "OK")
 *   cancelText     — cancel button label  (default: "Cancel")
 *   variant        — "default" | "danger" (red confirm button for destructive actions)
 *   onConfirm      — called when user clicks the confirm button or presses Enter
 *   onCancel       — called when user clicks the cancel button, the backdrop, or presses Esc
 *
 * Keyboard:
 *   - Enter      → onConfirm
 *   - Escape     → onCancel
 *   - Backdrop click → onCancel
 */
function ConfirmModal({
  isVisible,
  title,
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  variant = "default",
  onConfirm,
  onCancel,
}) {
  const confirmButtonRef = useRef(null);

  useEffect(() => {
    if (!isVisible) return;
    // Focus the primary action so Enter works immediately.
    confirmButtonRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel?.();
      } else if (e.key === "Enter") {
        // Only trigger if focus is inside the modal (avoids hijacking Enter
        // for editors / inputs that happen to be mounted underneath).
        if (
          document.activeElement &&
          confirmButtonRef.current &&
          confirmButtonRef.current.closest("[role=\"dialog\"]")?.contains(document.activeElement)
        ) {
          e.preventDefault();
          e.stopPropagation();
          onConfirm?.();
        }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [isVisible, onConfirm, onCancel]);

  if (!isVisible) return null;

  const confirmClass =
    variant === "danger"
      ? "bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white focus:ring-red-400"
      : "bg-[#CBEA7B] dark:bg-[#A4C639] text-gray-900 dark:text-gray-100 hover:opacity-90 focus:ring-green-400";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "confirm-modal-title" : undefined}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4"
      onMouseDown={(e) => {
        // Backdrop click cancels; clicks on the inner dialog don't bubble here.
        if (e.target === e.currentTarget) onCancel?.();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg max-w-sm w-full"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {title && (
          <h3
            id="confirm-modal-title"
            className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-200"
          >
            {title}
          </h3>
        )}
        {message && (
          <div className="text-sm text-gray-700 dark:text-gray-300">
            {message}
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded text-gray-900 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400"
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            className={`px-4 py-2 rounded transition-colors focus:outline-none focus:ring-2 ${confirmClass}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
